import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ScenarioConfigInput } from "@unbox/api";
import { handleError } from "@/api/error";
import { client } from "../client";

const SCENARIOS_KEY = ["admin", "scenarios"] as const;

/** The brands a workspace can hold and the seed scenarios a scenario can fill. */
export const useScenarioOptions = (enabled = true) => {
  return useQuery({
    queryKey: [...SCENARIOS_KEY, "options"],
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const res = await client.admin.scenarios.options.$get();
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

/** Scenarios that have been created, each with the user it made. */
export const useScenarioRuns = (enabled = true) => {
  return useQuery({
    queryKey: [...SCENARIOS_KEY, "runs"],
    enabled,
    queryFn: async () => {
      const res = await client.admin.scenarios.$get();
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

export const useCreateScenario = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; password?: string; config: ScenarioConfigInput }) => {
      const res = await client.admin.scenarios.$post({ json: payload });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...SCENARIOS_KEY, "runs"] }),
  });
};

export const useSavedScenarioConfigs = (enabled = true) => {
  return useQuery({
    queryKey: [...SCENARIOS_KEY, "configs"],
    enabled,
    queryFn: async () => {
      const res = await client.admin.scenarios.configs.$get();
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

export const useSaveScenarioConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; config: ScenarioConfigInput }) => {
      const res = await client.admin.scenarios.configs.$put({ json: payload });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...SCENARIOS_KEY, "configs"] }),
  });
};

export const useDeleteScenarioConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.admin.scenarios.configs[":id"].$delete({ param: { id } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...SCENARIOS_KEY, "configs"] }),
  });
};
